import { Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { PageHeader } from '@/components/shared/page-header';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import {
  type GlobalSearchAccess,
  type GlobalSearchScope,
  useGlobalSearch,
} from '@/hooks/use-global-search';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';

function getSearchSectionLabel(
  t: ReturnType<typeof useTranslation>['t'],
  scope: GlobalSearchScope,
) {
  switch (scope) {
    case 'members':
      return t('nav.people');
    case 'departments':
      return t('search.departments');
    case 'groups':
      return t('nav.groups');
    case 'services':
      return t('nav.services');
    case 'programs':
      return t('nav.programs');
    case 'funds':
      return t('search.funds');
    case 'contributions':
      return t('search.contributions');
    case 'pledges':
      return t('nav.pledges');
    case 'projects':
      return t('search.projects');
    case 'expenses':
      return t('nav.expenses');
    case 'registers':
      return t('nav.sacraments');
    case 'assets':
      return t('nav.assets');
    case 'documents':
      return t('nav.documents');
    case 'visits':
      return t('search.visits');
    default:
      return '';
  }
}

export default function GlobalSearchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryFromUrl = searchParams.get('q') ?? '';
  const [query, setQuery] = useState(queryFromUrl);
  const debouncedQuery = useDebouncedValue(query.trim(), 180);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const membership = useCurrentMembership();

  const access = useMemo<GlobalSearchAccess>(() => {
    const can = (moduleCode: string, permission: string) =>
      isModuleEnabled(moduleCode) && hasPermission(membership, permission);

    return {
      members: can('people', PERMISSIONS.PEOPLE_VIEW),
      departments: can('people', PERMISSIONS.PEOPLE_VIEW),
      groups: can('groups', PERMISSIONS.GROUPS_VIEW),
      services: can('programs', PERMISSIONS.PROGRAMS_VIEW),
      programs: can('programs', PERMISSIONS.PROGRAMS_VIEW),
      funds: can('giving', PERMISSIONS.GIVING_VIEW),
      contributions: can('giving', PERMISSIONS.GIVING_VIEW),
      pledges: can('giving', PERMISSIONS.GIVING_VIEW),
      projects: can('projects', PERMISSIONS.PROJECTS_VIEW),
      expenses: can('spending', PERMISSIONS.EXPENSES_VIEW),
      registers: can('sacraments', PERMISSIONS.SACRAMENTS_VIEW),
      assets: can('assets', PERMISSIONS.ASSETS_VIEW),
      documents: can('documents', PERMISSIONS.DOCUMENTS_VIEW),
      visits: can('pastoral', PERMISSIONS.PASTORAL_VIEW),
    };
  }, [isModuleEnabled, membership]);

  const searchResults = useGlobalSearch({
    query: debouncedQuery,
    limit: 12,
    access,
  });

  useEffect(() => {
    setQuery(queryFromUrl);
  }, [queryFromUrl]);

  useEffect(() => {
    if (debouncedQuery === queryFromUrl) return;

    const nextParams = new URLSearchParams(searchParams);
    if (debouncedQuery) {
      nextParams.set('q', debouncedQuery);
    } else {
      nextParams.delete('q');
    }
    setSearchParams(nextParams, { replace: true });
  }, [debouncedQuery, queryFromUrl, searchParams, setSearchParams]);

  const visibleSections = (searchResults.data ?? []).filter(
    (section) => section.items.length > 0 || section.hasError,
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('search.resultsTitle')}
        description={
          debouncedQuery.length >= 2
            ? t('search.resultsFor', { query: debouncedQuery })
            : t('search.resultsDescription')
        }
        tipId="search.scope"
      />

      <Card>
        <CardContent className="pt-6">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('search.globalPlaceholder')}
            prefix={<Search className="size-4" aria-hidden />}
            aria-label={t('search.globalPlaceholder')}
          />
        </CardContent>
      </Card>

      {debouncedQuery.length < 2 ? (
        <Card>
          <CardContent className="py-10 text-center text-body-sm text-fg-secondary">
            {t('search.startTyping')}
          </CardContent>
        </Card>
      ) : null}

      {debouncedQuery.length >= 2 && searchResults.isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center gap-2 py-10 text-body-sm text-fg-secondary">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {t('search.loading')}
          </CardContent>
        </Card>
      ) : null}

      {debouncedQuery.length >= 2 && !searchResults.isLoading && visibleSections.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-body-sm text-fg-secondary">
            {t('state.noSearchResults', { query: debouncedQuery })}
          </CardContent>
        </Card>
      ) : null}

      {visibleSections.map((section) => (
        <Card key={section.scope}>
          <CardContent className="space-y-1 pt-6">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 className="text-card-title font-medium">
                {getSearchSectionLabel(t, section.scope)}
              </h2>
              {section.hasError ? (
                <p className="text-caption text-danger">{t('state.errorTitle')}</p>
              ) : (
                <p className="text-caption text-fg-tertiary">
                  {t('search.matchesCount', { count: section.total })}
                </p>
              )}
            </div>

            {section.items.map((item) => (
              <button
                key={`${section.scope}-${item.id}`}
                type="button"
                className="flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-surface-sunken"
                onClick={() => navigate(item.href)}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body font-medium">{item.title}</span>
                  {item.subtitle ? (
                    <span className="block truncate text-caption text-fg-secondary">
                      {item.subtitle}
                    </span>
                  ) : null}
                  {item.detail ? (
                    <span className="block truncate text-caption text-fg-tertiary">
                      {item.detail}
                    </span>
                  ) : null}
                </span>
              </button>
            ))}

            {!section.hasError && section.total > section.items.length ? (
              <p className="px-3 pt-2 text-caption text-fg-tertiary">
                {t('search.showingTopResults', { count: section.items.length })}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
